/** Mirrors the API's admin adjustment shapes. Kept here because they cross api → hook → component. */

export type AdjustmentDirection = "add" | "remove";
export type DrawOrder = "fifo" | "lifo";

/** approval_requests.status. */
export const ApprovalStatus = {
  pending: 0,
  approved: 1,
  rejected: 2,
  cancelled: 3,
} as const;
export type ApprovalStatusValue = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const APPROVAL_STATUS_LABEL: Record<number, string> = {
  0: "Pending",
  1: "Approved",
  2: "Rejected",
  3: "Cancelled",
};

/** A reason the picker may offer. `direction` decides the sign the form submits. */
export interface RaisableReason {
  code: string;
  label: string | null;
  description: string | null;
  direction: AdjustmentDirection;
}

export interface AdjustmentAttachment {
  id: number;
  fileLocation: string;
  proofNature: string | null;
  uploadedBy: number | null;
  createdAt: string;
}

/** One row of the Adjustments blotter. `amount` is CREDITS, signed. */
export interface Adjustment {
  id: number;
  reasonCode: string | null;
  reasonLabel: string | null;
  targetType: string;
  targetId: number;
  groupName: string | null;
  organizationName: string | null;
  amount: string;
  ccy: string | null;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
  appliedAt: string | null;
  resultType: string | null;
  resultId: number | null;
  approvalRequestId: number | null;
  status: number | null;
}

/** A checker's verdict, as the audit trail records it. */
export interface AdjustmentDecision {
  id: number;
  decision: number;
  note: string | null;
  decidedAt: string;
  approverName: string | null;
}

export interface AdjustmentDetail extends Adjustment {
  attachments: AdjustmentAttachment[];
  decisions: AdjustmentDecision[];
  /** The draw order the maker chose, for a removal. Null on an add. */
  drawOrder: string | null;
}

/** An option for the "raised by" filter. */
export interface AdjustmentRaiser {
  id: number;
  name: string | null;
}

/** What the raise form submits. `amount` is signed by the reason's direction, not by the user. */
export interface RaiseAdjustmentPayload {
  reasonCode: string;
  targetType: "group";
  targetId: number;
  amount: string;
  ccy?: string;
  note?: string;
  drawOrder?: DrawOrder;
  attachments?: { fileLocation: string; proofNature?: string }[];
}

/**
 * A queue item. `actionable` is false for a request the caller raised — the server refuses those
 * outright, so this only decides whether the buttons render.
 */
/** One labelled thing a checker needs before deciding. */
export interface ActionFact {
  label: string;
  value: string;
  /** `warn` gives the approver pause; `muted` is context, not part of the change. */
  tone?: "warn" | "muted";
}

/** How a queued action describes itself, resolved server-side by that action's own summarizer. */
export interface ActionSummary {
  headline: string;
  detail: string | null;
  /** Whether this adds or takes away — what the card colours on. */
  variant?: "positive" | "negative";
  facts?: ActionFact[];
  /** A second column: what the change is decided against (exposure, limit, headroom). */
  sideFacts?: ActionFact[];
  /** The maker's own words, shown quoted. */
  note?: string | null;
  /** Shown when the checker is asked to commit. */
  confirmPrompt?: string;
}

export interface QueueItem {
  id: number;
  actionType: string;
  actionId: number;
  requestedBy: number;
  requestedByName: string | null;
  status: number;
  createdAt: string;
  resolvedAt: string | null;
  actionable: boolean;
  /** The caller raised this. Only they may withdraw it — never the inverse of `actionable`. */
  isMine: boolean;
  /** Null when the action type has no summarizer registered server-side. */
  summary: ActionSummary | null;
  /** Human label for the action type — what the filter tiles are named after. */
  actionLabel: string;
}

export interface AdjustmentListQuery {
  search?: string;
  status?: number;
  groupId?: number;
  organizationId?: number;
  createdBy?: number;
  raisedFrom?: string;
  raisedTo?: string;
  page?: number;
  limit?: number;
}

/** A member of the checker pool. Inactive members are kept so past decisions still resolve. */
export interface Approver {
  id: number;
  userId: number;
  name: string | null;
  email: string | null;
  isActive: boolean;
  addedBy: number | null;
  createdAt: string;
}
